import { useState, useEffect, useRef } from "react";
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

// ─── Styles ──────────────────────────────────────────────────────────────────

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');

  .srw-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .srw-root {
    font-family: 'IBM Plex Sans', sans-serif;
    background: #f5f4f0;
    min-height: 100vh;
    padding: 0.75rem;
    color: #1a1a18;
  }
  @media (min-width: 640px) { .srw-root { padding: 2rem 1rem; } }

  .srw-sheet {
    max-width: 860px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid #d6d4cc;
    border-radius: 4px;
  }

  /* ── Masthead ── */
  .srw-masthead {
    background: #1a1a18;
    color: #f5f4f0;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  @media (min-width: 640px) {
    .srw-masthead { padding: 1.25rem 1.75rem; flex-direction: row; justify-content: space-between; align-items: flex-start; }
  }
  .srw-masthead-title { font-size: 15px; font-weight: 500; letter-spacing: 0.02em; line-height: 1.3; }
  @media (min-width: 640px) { .srw-masthead-title { font-size: 16px; } }
  .srw-masthead-sub { font-size: 11px; color: #9e9c94; margin-top: 4px; font-family: 'IBM Plex Mono', monospace; line-height: 1.4; }
  .srw-masthead-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }

  /* ── Buttons ── */
  .srw-btn {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    padding: 8px 14px;
    border-radius: 2px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
  }
  .srw-btn-ghost { border: 1px solid #4a4a44; background: transparent; color: #9e9c94; }
  .srw-btn-ghost:hover { border-color: #9e9c94; color: #f5f4f0; }
  .srw-btn-primary { border: 1px solid #b08a10; background: #b08a10; color: #fff; }
  .srw-btn-primary:hover { background: #c99b15; border-color: #c99b15; }
  .srw-btn-primary:disabled { background: #4a4a44; border-color: #4a4a44; color: #7a7870; cursor: not-allowed; }
  .srw-btn-danger { border: 1px solid #c94444; background: transparent; color: #c94444; }
  .srw-btn-danger:hover { background: #c94444; color: #fff; }

  /* ── Admin drawer ── */
  .srw-admin-bar {
    background: #f0f6ff;
    border-bottom: 1px solid #b8d0f0;
    padding: 12px 1rem;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  @media (min-width: 640px) { .srw-admin-bar { flex-direction: row; align-items: center; padding: 12px 1.75rem; } }
  .srw-admin-bar-label { font-family: 'IBM Plex Mono', monospace; font-size: 11px; color: #3370c4; font-weight: 500; letter-spacing: 0.05em; text-transform: uppercase; flex-shrink: 0; }
  .srw-admin-search { flex: 1; background: #fff; border: 1px solid #b8d0f0; border-radius: 2px; padding: 9px 10px; font-size: 14px; font-family: 'IBM Plex Sans', sans-serif; color: #1a1a18; width: 100%; }
  .srw-admin-search:focus { outline: none; border-color: #3370c4; }
  .srw-records-list { border-top: 1px solid #e0ded8; max-height: 320px; overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .srw-record-row { padding: 12px 1rem; border-bottom: 1px solid #f0ede8; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; cursor: pointer; transition: background 0.1s; -webkit-tap-highlight-color: transparent; }
  @media (min-width: 640px) { .srw-record-row { padding: 10px 1.75rem; align-items: center; } }
  .srw-record-row:hover, .srw-record-row:active { background: #f5f4f0; }
  .srw-record-name { font-size: 13px; font-weight: 500; }
  .srw-record-meta { font-size: 11px; color: #7a7870; font-family: 'IBM Plex Mono', monospace; margin-top: 3px; }
  .srw-record-date { font-size: 11px; color: #7a7870; font-family: 'IBM Plex Mono', monospace; flex-shrink: 0; text-align: right; }
  .srw-records-empty { padding: 1.5rem 1rem; font-size: 13px; color: #7a7870; font-family: 'IBM Plex Mono', monospace; }

  /* ── Status ── */
  .srw-status { padding: 10px 1rem; font-size: 12px; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.03em; line-height: 1.4; }
  @media (min-width: 640px) { .srw-status { padding: 10px 1.75rem; } }
  .srw-status.success { background: #f0fff4; color: #1a7a40; border-bottom: 1px solid #a8e6bf; }
  .srw-status.error   { background: #fff5f5; color: #c94444; border-bottom: 1px solid #f5c0c0; }
  .srw-status.saving  { background: #fffbf0; color: #b08a10; border-bottom: 1px solid #f0dfa0; }

  /* ── Body ── */
  .srw-body { padding: 1.25rem 1rem; }
  @media (min-width: 640px) { .srw-body { padding: 1.5rem 1.75rem; } }

  .srw-section { margin-bottom: 1.75rem; }
  .srw-section-label { font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: #7a7870; border-bottom: 1px solid #e0ded8; padding-bottom: 6px; margin-bottom: 12px; }

  /* ── Mobile-first grid — stacks to 1 col, expands at breakpoints ── */
  .srw-grid { display: grid; gap: 10px; margin-bottom: 10px; grid-template-columns: 1fr; }
  @media (min-width: 480px) { .srw-grid.c2 { grid-template-columns: 1fr 1fr; } }
  @media (min-width: 480px) { .srw-grid.c3 { grid-template-columns: 1fr 1fr; } }
  @media (min-width: 680px) { .srw-grid.c3 { grid-template-columns: 1fr 1fr 1fr; } }

  /* ── Fields ── */
  .srw-field { display: flex; flex-direction: column; gap: 4px; }
  .srw-field label { font-size: 11px; color: #7a7870; font-family: 'IBM Plex Mono', monospace; }
  .srw-field input,
  .srw-field select,
  .srw-field textarea {
    background: #faf9f6;
    border: 1px solid #d6d4cc;
    border-radius: 2px;
    padding: 9px 10px;
    font-size: 14px;
    color: #1a1a18;
    font-family: 'IBM Plex Sans', sans-serif;
    width: 100%;
    transition: border-color 0.15s;
    -webkit-appearance: none;
    appearance: none;
  }
  .srw-field select {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%237a7870' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 28px;
  }
  .srw-field input:focus, .srw-field select:focus, .srw-field textarea:focus { outline: none; border-color: #1a1a18; background: #fff; }
  .srw-field input[readonly] { color: #7a7870; background: #f0ede8; cursor: default; }
  .srw-field textarea { resize: vertical; min-height: 80px; line-height: 1.5; }

  /* ── Student picker ── */
  .srw-picker-wrap { position: relative; }
  .srw-picker-input { background: #faf9f6; border: 1px solid #d6d4cc; border-radius: 2px; padding: 9px 10px; font-size: 14px; color: #1a1a18; font-family: 'IBM Plex Sans', sans-serif; width: 100%; transition: border-color 0.15s; -webkit-appearance: none; }
  .srw-picker-input:focus { outline: none; border-color: #1a1a18; background: #fff; }
  .srw-picker-dropdown { position: absolute; top: calc(100% + 2px); left: 0; right: 0; background: #fff; border: 1px solid #d6d4cc; border-radius: 2px; z-index: 200; max-height: 260px; overflow-y: auto; box-shadow: 0 4px 16px rgba(0,0,0,0.12); -webkit-overflow-scrolling: touch; }
  .srw-picker-option { padding: 12px 14px; font-size: 14px; cursor: pointer; display: flex; justify-content: space-between; align-items: center; transition: background 0.1s; border-bottom: 1px solid #f0ede8; -webkit-tap-highlight-color: transparent; }
  .srw-picker-option:last-child { border-bottom: none; }
  .srw-picker-option:hover, .srw-picker-option:active { background: #f5f4f0; }
  .srw-picker-grade-badge { font-size: 10px; font-family: 'IBM Plex Mono', monospace; background: #e0ded8; padding: 3px 7px; border-radius: 2px; color: #5a5850; flex-shrink: 0; margin-left: 8px; }
  .srw-picker-empty { padding: 14px; font-size: 13px; color: #9e9c94; font-family: 'IBM Plex Mono', monospace; }
  .srw-grade-filter { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
  .srw-grade-chip { font-family: 'IBM Plex Mono', monospace; font-size: 11px; padding: 6px 10px; border: 1px solid #d6d4cc; border-radius: 2px; background: #faf9f6; cursor: pointer; transition: all 0.1s; color: #5a5850; min-height: 32px; display: inline-flex; align-items: center; -webkit-tap-highlight-color: transparent; }
  .srw-grade-chip:hover { border-color: #1a1a18; color: #1a1a18; }
  .srw-grade-chip.selected { background: #1a1a18; color: #f5f4f0; border-color: #1a1a18; }

  /* ── Checkboxes ── */
  .srw-cb-group { display: flex; flex-wrap: wrap; gap: 10px 18px; }
  .srw-cb-item { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; cursor: pointer; line-height: 1.4; -webkit-tap-highlight-color: transparent; }
  .srw-cb-item input[type=checkbox] { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; accent-color: #1a1a18; cursor: pointer; }
  .srw-cb-sublabel { font-size: 11px; color: #7a7870; font-family: 'IBM Plex Mono', monospace; margin-bottom: 8px; }

  /* ── Alert boxes ── */
  .srw-flag { background: #fff5f5; border: 1px solid #f5c0c0; border-left: 3px solid #c94444; border-radius: 2px; padding: 12px 14px; margin-bottom: 10px; }
  .srw-flag-title { font-size: 11px; font-weight: 500; color: #c94444; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px; }
  .srw-info { background: #f0f6ff; border: 1px solid #b8d0f0; border-left: 3px solid #3370c4; border-radius: 2px; padding: 12px 14px; margin-bottom: 10px; }
  .srw-info-title { font-size: 11px; font-weight: 500; color: #3370c4; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px; }
  .srw-warn { background: #fffbf0; border: 1px solid #f0dfa0; border-left: 3px solid #b08a10; border-radius: 2px; padding: 12px 14px; margin-bottom: 10px; }
  .srw-warn-title { font-size: 11px; font-weight: 500; color: #b08a10; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px; }

  /* ── Off-campus callout ── */
  .srw-offcampus { background: #f8f4ff; border: 1px solid #c8b0e8; border-left: 3px solid #7040b0; border-radius: 2px; padding: 14px; margin-top: 12px; }
  .srw-offcampus-title { font-size: 11px; font-weight: 500; color: #7040b0; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 12px; }
  .srw-offcampus-note { margin-top: 10px; padding: 8px 10px; background: #f0ede8; border-radius: 2px; font-size: 11px; font-family: 'IBM Plex Mono', monospace; color: #7040b0; line-height: 1.5; }

  /* ── Photo uploader ── */
  .srw-photo-note { font-size: 12px; font-family: 'IBM Plex Mono', monospace; color: #7a7870; margin-bottom: 12px; line-height: 1.5; }

  /* Mobile tap targets: two big buttons */
  .srw-photo-mobile-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
  @media (min-width: 640px) { .srw-photo-mobile-actions { display: none; } }

  .srw-photo-tap-btn {
    padding: 18px 10px;
    border: 1px solid #d6d4cc;
    border-radius: 4px;
    background: #faf9f6;
    cursor: pointer;
    text-align: center;
    transition: all 0.15s;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    position: relative;
    -webkit-tap-highlight-color: transparent;
  }
  .srw-photo-tap-btn:active { border-color: #1a1a18; background: #f0ede8; }
  .srw-photo-tap-btn input[type=file] { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer; font-size: 0; }
  .srw-photo-tap-icon { font-size: 26px; line-height: 1; }
  .srw-photo-tap-label { font-size: 12px; font-family: 'IBM Plex Mono', monospace; color: #1a1a18; }

  /* Desktop drop zone */
  .srw-photo-desktop-zone { display: none; }
  @media (min-width: 640px) { .srw-photo-desktop-zone { display: block; } }
  .srw-photo-drop-area {
    border: 2px dashed #d6d4cc;
    border-radius: 4px;
    padding: 24px 16px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    background: #faf9f6;
    position: relative;
    margin-bottom: 10px;
  }
  .srw-photo-drop-area:hover, .srw-photo-drop-area.dragover { border-color: #1a1a18; background: #f0ede8; }
  .srw-photo-drop-area input[type=file] { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }
  .srw-photo-drop-icon { font-size: 28px; margin-bottom: 8px; }
  .srw-photo-drop-label { font-size: 13px; color: #5a5850; }
  .srw-photo-drop-sub { font-size: 11px; color: #9e9c94; font-family: 'IBM Plex Mono', monospace; margin-top: 4px; }

  .srw-photo-uploading-msg { font-size: 12px; font-family: 'IBM Plex Mono', monospace; color: #b08a10; margin: 8px 0; }

  /* Photo grid */
  .srw-photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 10px; }
  @media (min-width: 480px) { .srw-photo-grid { grid-template-columns: repeat(4, 1fr); } }
  @media (min-width: 640px) { .srw-photo-grid { grid-template-columns: repeat(5, 1fr); } }

  .srw-photo-cell { display: flex; flex-direction: column; gap: 3px; }
  .srw-photo-thumb { position: relative; aspect-ratio: 1; border-radius: 3px; overflow: hidden; background: #e0ded8; }
  .srw-photo-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .srw-photo-del {
    position: absolute; top: 4px; right: 4px;
    background: rgba(0,0,0,0.65); color: #fff;
    border: none; border-radius: 50%;
    width: 24px; height: 24px; font-size: 15px;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    line-height: 1; -webkit-tap-highlight-color: transparent;
  }
  .srw-photo-caption { font-size: 10px; font-family: 'IBM Plex Mono', monospace; color: #9e9c94; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .srw-photo-none { font-size: 12px; color: #9e9c94; font-family: 'IBM Plex Mono', monospace; margin-top: 6px; }

  /* ── Misc ── */
  .srw-divider { border: none; border-top: 1px solid #e0ded8; margin: 1.5rem 0; }
  .srw-sign-row { display: grid; grid-template-columns: 1fr; gap: 16px; }
  @media (min-width: 480px) { .srw-sign-row { grid-template-columns: 1fr 1fr; } }
  @media (min-width: 640px) { .srw-sign-row { grid-template-columns: 1fr 1fr 1fr; } }
  .srw-sign-field { border-top: 1px solid #1a1a18; padding-top: 8px; font-size: 11px; color: #7a7870; font-family: 'IBM Plex Mono', monospace; min-height: 44px; }

  .srw-footer { text-align: center; font-size: 10px; color: #aaa9a2; font-family: 'IBM Plex Mono', monospace; padding: 1rem; border-top: 1px solid #e0ded8; line-height: 1.6; }
  .srw-readonly-banner { background: #1a1a18; color: #f5f4f0; text-align: center; padding: 9px 12px; font-size: 11px; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.04em; line-height: 1.4; }

  /* ── Print ── */
  @media print {
    .srw-root { background: #fff; padding: 0; }
    .srw-btn, .srw-admin-bar, .srw-records-list, .srw-masthead-actions,
    .srw-grade-filter, .srw-status, .srw-readonly-banner,
    .srw-photo-mobile-actions, .srw-photo-drop-area, .srw-photo-del,
    .srw-photo-desktop-zone { display: none !important; }
    .srw-sheet { border: none; }
    .srw-picker-input, .srw-field input, .srw-field select, .srw-field textarea {
      border: none !important; background: transparent !important; padding: 2px 0 !important;
      -webkit-appearance: none; appearance: none;
    }
    .srw-sign-row { grid-template-columns: 1fr 1fr 1fr !important; }
    .srw-photo-grid { grid-template-columns: repeat(4, 1fr) !important; }
    .srw-grid.c2 { grid-template-columns: 1fr 1fr !important; }
    .srw-grid.c3 { grid-template-columns: 1fr 1fr 1fr !important; }
    .srw-offcampus { border: 1px solid #c8b0e8 !important; }
  }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const GRADES = ["7", "8", "9", "10", "11", "12"];

const INCIDENT_REASONS = [
  "Physical aggression toward student",
  "Physical aggression toward staff",
  "Verbal abuse / threats",
  "Targeted harassment / bullying",
  "Self-harm / danger to self",
  "Weapons possession",
  "Controlled substance (possession / use)",
  "Destruction of property",
  "Persistent refusal / defiance",
  "Disruption preventing others from participating",
  "Unsafe behavior (context-specific risk)",
  "Other (describe below)",
];

const MANDATORY_FLAGS = [
  "Weapons involved → report to administration immediately",
  "Controlled substance → report per school drug policy",
  "Credible threat of violence → safety officer / law enforcement",
  "Suspected child abuse / neglect indicators → mandated reporter obligation",
  "Self-harm / suicidal ideation → counselor and parent same day",
];

const ACADEMIC_CONTINUITY = [
  "Work / assignments provided for time out of class",
  "Academic support arranged for missed instruction",
  "Grading / assessment accommodation needed",
];

const PARENT_CONTACT_METHODS = [
  "Parent / guardian contacted by phone",
  "Voicemail left (follow up in writing)",
  "Written notice sent home with student",
  "Email sent",
  "In-person meeting scheduled",
  "Parent / guardian came to school (same day)",
  "Interpreter needed / used",
];

const OFF_CAMPUS_ACTIVITY_TYPES = [
  "Field trip (academic)",
  "Athletic competition / away game",
  "Performing arts / fine arts event",
  "Community service",
  "College visit",
  "Overnight / multi-day trip",
  "Transportation only (bus / van)",
  "Other",
];

const OFF_CAMPUS_AUTHORITY_OPTIONS = [
  "Local police / law enforcement notified",
  "Venue / host organization security notified",
  "Emergency services (ambulance / fire) called",
  "No external authorities involved",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div className="srw-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="srw-section-label">{children}</div>;
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    function onDown(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [ref, handler]);
}

// ─── Student Picker ───────────────────────────────────────────────────────────

function StudentPicker({ students, value, onChange, readOnly }) {
  const [searchText, setSearchText] = useState("");
  const [gradeFilter, setGradeFilter] = useState(null);
  const [open, setOpen] = useState(false); 

  const selectedStudent = students.find((s) => s.sisId === value?.sisId) || null;

    const filtered = students
    .filter((s) => {
        const matchGrade = gradeFilter ? s.grade === `Grade ${gradeFilter}` : true;
        const q = searchText.toLowerCase();
        const matchQuery =
        !searchText ||
        s.firstName?.toLowerCase().includes(q) ||
        s.lastName?.toLowerCase().includes(q) ||
        s.sisId?.toLowerCase().includes(q);
        return matchGrade && matchQuery;
    })
    .sort((a, b) => a.firstName?.localeCompare(b.firstName));

  if (readOnly) {
    return (
      <input
        className="srw-picker-input"
        value={
          selectedStudent
            ? `${selectedStudent.lastName}, ${selectedStudent.firstName}`
            : value?.displayName || ""
        }
        readOnly
      />
    );
  }

  if (selectedStudent) {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="srw-picker-input"
          value={`${selectedStudent.lastName}, ${selectedStudent.firstName}`}
          readOnly
          style={{ flex: 1 }}
        />
        <button
          style={{
            padding: "9px 12px", border: "1px solid #d6d4cc", borderRadius: 2,
            background: "#faf9f6", cursor: "pointer", fontFamily: "IBM Plex Mono, monospace",
            fontSize: 11, color: "#c94444", whiteSpace: "nowrap"
          }}
          onClick={() => { onChange(null); setSearchText(""); }}
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="srw-grade-filter">
        <span
          className={`srw-grade-chip${!gradeFilter ? " selected" : ""}`}
          onClick={() => { setGradeFilter(null); setOpen(true); }}
        >All</span>
        {GRADES.map((g) => (
          <span
            key={g}
            className={`srw-grade-chip${gradeFilter === g ? " selected" : ""}`}
            onClick={() => { setGradeFilter(g === gradeFilter ? null : g); setOpen(true); }}
          >
            Gr {g}
          </span>
        ))}
      </div>
      <input
        className="srw-picker-input"
        placeholder="Search name or SIS ID…"
        value={searchText}
        onChange={(e) => { setSearchText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        style={{ marginBottom: 6 }}
      />
      {open && (  // ← wrap list in this
        <div style={{
          border: "1px solid #d6d4cc", borderRadius: 2, maxHeight: 220,
          overflowY: "auto", background: "#fff"
        }}>
          {filtered.length === 0 ? (
            <div className="srw-picker-empty">No students found</div>
          ) : (
            filtered.map((s) => (
              <div
                key={s.sisId}
                className="srw-picker-option"
                onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
              >
                <span>
                  {s.lastName}, {s.firstName}
                  <span style={{ color: "#9e9c94", marginLeft: 8, fontSize: 11 }}>{s.sisId}</span>
                </span>
                <span className="srw-picker-grade-badge">{s.grade}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Staff Picker ───────────────────────────────────────────────────────────
function StaffPicker({ staff, value, onChange, readOnly }) {
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);

  const selectedStaff = staff.find((s) => s.sisId === value?.sisId) || null;

  const filtered = staff
    .filter((s) => {
      const q = searchText.toLowerCase();
      return (
        !searchText ||
        s.name?.toLowerCase().includes(q) ||
        s.sisId?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => a.name?.localeCompare(b.name));

  if (readOnly) {
    return (
      <input
        className="srw-picker-input"
        value={selectedStaff?.name || value?.name || ""}
        readOnly
      />
    );
  }

  if (selectedStaff) {
    return (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="srw-picker-input"
          value={selectedStaff.name}
          readOnly
          style={{ flex: 1 }}
        />
        <button
          style={{
            padding: "9px 12px", border: "1px solid #d6d4cc", borderRadius: 2,
            background: "#faf9f6", cursor: "pointer", fontFamily: "IBM Plex Mono, monospace",
            fontSize: 11, color: "#c94444", whiteSpace: "nowrap"
          }}
          onClick={() => { onChange(null); setSearchText(""); }}
        >
          Clear
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        className="srw-picker-input"
        placeholder="Search name or SIS ID…"
        value={searchText}
        onChange={(e) => { setSearchText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        style={{ marginBottom: 6 }}
      />
      {open && (  // ← wrap list in this
        <div style={{
          border: "1px solid #d6d4cc", borderRadius: 2, maxHeight: 220,
          overflowY: "auto", background: "#fff"
        }}>
          {filtered.length === 0 ? (
            <div className="srw-picker-empty">No staff found</div>
          ) : (
            filtered.map((s) => (
              <div
                key={s.sisId}
                className="srw-picker-option"
                onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
              >
                <span>
                  {s.name}
                  <span style={{ color: "#9e9c94", marginLeft: 8, fontSize: 11 }}>{s.sisId}</span>
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Photo Uploader ───────────────────────────────────────────────────────────

function PhotoUploader({ photos, onChange, readOnly, tempFolder }) {
  const [uploadingCount, setUploadingCount] = useState(0);
  const dropRef = useRef(null);

  async function handleFiles(fileList) {
    const validFiles = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!validFiles.length) return;

    setUploadingCount((n) => n + validFiles.length);

    for (const file of validFiles) {
      const uid = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      try {
        const storagePath = `studentActionPhotos/${tempFolder}/${uid}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        onChange((prev) => [...prev, { url, storagePath, name: file.name, uid }]);
      } catch (err) {
        console.error("Photo upload failed:", err);
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
  }

  async function removePhoto(photo) {
    try {
      if (photo.storagePath) {
        await deleteObject(ref(storage, photo.storagePath));
      }
    } catch (_) { /* already deleted or doesn't exist */ }
    onChange((prev) => prev.filter((p) => (p.uid || p.url) !== (photo.uid || photo.url)));
  }

  return (
    <div>
      {!readOnly && (
        <>
          {/* ── Mobile: two large tap buttons ── */}
          <div className="srw-photo-mobile-actions">
            <div className="srw-photo-tap-btn">
              <span className="srw-photo-tap-icon">📷</span>
              <span className="srw-photo-tap-label">Take Photo</span>
              <input type="file" accept="image/*" capture="environment" multiple onChange={(e) => handleFiles(e.target.files)} />
            </div>
            <div className="srw-photo-tap-btn">
              <span className="srw-photo-tap-icon">🖼️</span>
              <span className="srw-photo-tap-label">Photo Library</span>
              <input type="file" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} />
            </div>
          </div>

          {/* ── Desktop: drag-and-drop zone ── */}
          <div className="srw-photo-desktop-zone">
            <div
              ref={dropRef}
              className="srw-photo-drop-area"
              onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("dragover"); }}
              onDragLeave={() => dropRef.current?.classList.remove("dragover")}
              onDrop={(e) => { e.preventDefault(); dropRef.current?.classList.remove("dragover"); handleFiles(e.dataTransfer.files); }}
            >
              <input type="file" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} />
              <div className="srw-photo-drop-icon">📎</div>
              <div className="srw-photo-drop-label">Click to attach photos, or drag and drop here</div>
              <div className="srw-photo-drop-sub">JPEG · PNG · HEIC · Multiple files supported</div>
            </div>
          </div>
        </>
      )}

      {uploadingCount > 0 && (
        <div className="srw-photo-uploading-msg">
          ⏳ Uploading {uploadingCount} photo{uploadingCount > 1 ? "s" : ""}…
        </div>
      )}

      {photos.length > 0 ? (
        <div className="srw-photo-grid">
          {photos.map((photo) => (
            <div key={photo.uid || photo.url} className="srw-photo-cell">
              <div className="srw-photo-thumb">
                <img src={photo.url} alt={photo.name || "Incident photo"} />
                {!readOnly && (
                  <button className="srw-photo-del" onClick={() => removePhoto(photo)} title="Remove">×</button>
                )}
              </div>
              <div className="srw-photo-caption">{photo.name || "photo"}</div>
            </div>
          ))}
        </div>
      ) : (
        readOnly && <div className="srw-photo-none">No photos attached to this record.</div>
      )}
    </div>
  );
}

// ─── Admin Records Drawer ─────────────────────────────────────────────────────

function AdminDrawer({ onLoad }) {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const q = query(collection(db, "studentActions"), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = records.filter((r) => {
    const s = search.toLowerCase();
    return !search || r.studentDisplayName?.toLowerCase().includes(s) || r.studentSisId?.toLowerCase().includes(s) || r.incidentDate?.includes(s) || r.staffInitiating?.toLowerCase().includes(s);
  });

  function fmtDate(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  return (
    <>
      <div className="srw-admin-bar">
        <span className="srw-admin-bar-label">Admin — Records</span>
        <input className="srw-admin-search" placeholder="Search name, SIS ID, date, or staff…" value={search} onChange={(e) => setSearch(e.target.value)} autoCorrect="off" spellCheck={false} />
      </div>
      <div className="srw-records-list">
        {loading ? (
          <div className="srw-records-empty">Loading records…</div>
        ) : filtered.length === 0 ? (
          <div className="srw-records-empty">No records found.</div>
        ) : (
          filtered.map((r) => (
            <div key={r.id} className="srw-record-row" onClick={() => onLoad(r)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="srw-record-name">
                  {r.studentDisplayName || "Unknown student"}
                  {r.offCampus && (
                    <span style={{ marginLeft: 8, fontSize: 10, background: "#f8f4ff", color: "#7040b0", border: "1px solid #c8b0e8", padding: "2px 6px", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace" }}>
                      OFF-CAMPUS
                    </span>
                  )}
                  {r.studentGrade && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: "#9e9c94", fontFamily: "IBM Plex Mono, monospace" }}>Gr {r.studentGrade}</span>
                  )}
                </div>
                <div className="srw-record-meta">
                  SIS: {r.studentSisId || "—"} · {r.staffInitiating || "—"}
                  {r.photos?.length > 0 && ` · 📎 ${r.photos.length}`}
                </div>
              </div>
              <div className="srw-record-date">{fmtDate(r.createdAt)}</div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ─── Empty form factory ───────────────────────────────────────────────────────

function emptyForm() {
  return {
    // Section 1
    incidentDate: "", timeOfRemoval: "", reportCompletedDate: "",
    studentSisId: "", studentDisplayName: "", studentGrade: "", studentIdGrade: "",
    staffInitiating: "", staffInitiatingSisId: "", administratorNotified: "", timeAdminNotified: "",    // Section 2 — setting
    settingType: "", specificLocation: "", subjectActivity: "",
    otherAdultsPresent: "", studentsPresent: "",
    // Off-campus
    offCampus: false,
    offCampusDestination: "", offCampusCity: "", offCampusCountry: "",
    offCampusActivityType: "",
    offCampusAdditionalSupervisors: "",
    offCampusParentConsent: "",
    offCampusAuthorities: {},
    offCampusHostContact: "",
    offCampusTransportDetails: "",
    offCampusReturnTime: "",
    offCampusInsuranceRef: "",
    offCampusNotes: "",
    // Section 3 — incident
    incidentReasons: {}, narrative: "", antecedent: "", priorRedirections: "",
    witnesses: "", othersAffected: "",
    // Flags
    mandatoryFlags: {}, learningPlan: "", learningPlanConsistent: "",
    // Section 4 — response
    studentSentTo: "", supervisedBy: "", studentDemeanor: "", physicalRestraint: "",
    actionsForRemaining: "",
    // Section 5 — parent
    parentContactMethods: {}, parentGuardianName: "", firstContactAttempt: "",
    contactMadeTime: "", conversationSummary: "", followUpMeeting: "", meetingDateTime: "",
    // Section 6 — next steps
    disciplinaryConsequence: "", consequenceDuration: "", conditionsForReturn: "",
    behavioralExpectations: "", behaviorSupportPlan: "", counselingReferral: "", checkInPlan: "",
    // Academic
    academicContinuity: {}, academicContinuityNotes: "",
    // Section 7 — reflection
    contributingFactors: "", patternHistory: "", previousIncidentDates: "",
    staffReflection: "", systemicChanges: "",
  };
}

// ─── Field render helpers (outside component to prevent remount on render) ────

function F({ label, fieldKey, form, setField, readOnly, type = "text", placeholder = "", inputMode }) {
  return (
    <Field label={label}>
      {readOnly
        ? <input type={type} value={form[fieldKey] || ""} readOnly />
        : <input type={type} placeholder={placeholder} value={form[fieldKey] || ""} inputMode={inputMode} autoComplete="off" onChange={(e) => setField(fieldKey, e.target.value)} />
      }
    </Field>
  );
}

function S({ label, fieldKey, options, form, setField, readOnly }) {
  return (
    <Field label={label}>
      {readOnly
        ? <input value={form[fieldKey] || ""} readOnly />
        : (
          <select value={form[fieldKey] || ""} onChange={(e) => setField(fieldKey, e.target.value)}>
            <option value="">— select —</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )
      }
    </Field>
  );
}

function T({ label, fieldKey, form, setField, readOnly, rows = 3, placeholder = "" }) {
  return (
    <Field label={label}>
      <textarea rows={rows} placeholder={readOnly ? "" : placeholder} value={form[fieldKey] || ""} readOnly={readOnly} onChange={readOnly ? undefined : (e) => setField(fieldKey, e.target.value)} />
    </Field>
  );
}

function CB({ groupKey, items, form, toggleCheck, readOnly }) {
  return (
    <div className="srw-cb-group">
      {items.map((item) => (
        <label key={item} className="srw-cb-item">
          <input type="checkbox" checked={!!form[groupKey]?.[item]} onChange={readOnly ? undefined : () => toggleCheck(groupKey, item)} readOnly={readOnly} />
          {item}
        </label>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function StudentAction() {
  const { currentUser, userRole } = useAuth();

  const isAdmin =
    userRole === "admin" ||
    userRole === "superadmin" ||
    currentUser?.uid === "IOj8t19zqBRJynu45HRKqf8J0MB2";

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [tempFolder] = useState(() => `temp_${Date.now()}`);
  const [showAdminDrawer, setShowAdminDrawer] = useState(false);
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);

        // Load student roster
        useEffect(() => {
        (async () => {
            try {
            const snap = await getDocs(collection(db, "students"));
            setStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (e) { console.error("Could not load students:", e); }
            finally { setStudentsLoading(false); }
        })();
        }, []);

        // Load staff roster
        useEffect(() => {
        (async () => {
            try {
            const snap = await getDocs(collection(db, "staff"));
            setStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (e) { console.error("Could not load staff:", e); }
        })();
        }, []);

        // Populate student fields when a student is chosen
        useEffect(() => {
        if (selectedStudent) {
            setForm((prev) => ({
            ...prev,
            studentSisId: selectedStudent.sisId || "",
            studentDisplayName: `${selectedStudent.lastName}, ${selectedStudent.firstName}`,
            studentGrade: selectedStudent.grade || "",
            studentIdGrade: `${selectedStudent.sisId} — ${selectedStudent.grade}`,
            }));
        }
        }, [selectedStudent]);

        // Populate staff fields when a staff member is chosen
        useEffect(() => {
        if (selectedStaff) {
            setForm((prev) => ({
            ...prev,
            staffInitiating: selectedStaff.name || "",
            staffInitiatingSisId: selectedStaff.sisId || "",
            }));
        }
        }, [selectedStaff]);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function toggleCheck(groupKey, item) {
    setForm((prev) => ({
      ...prev,
      [groupKey]: { ...prev[groupKey], [item]: !prev[groupKey]?.[item] },
    }));
  }

    function resetForm() {
    setForm(emptyForm());
    setSelectedStudent(null);
    setSelectedStaff(null);
    setViewingRecord(null);
    setPhotos([]);
    setStatus(null);
    }

  async function handleSave() {
  if (!form.studentDisplayName) {
    setStatus({ type: "error", msg: "Please select a student before saving." });
    return;
  }
  setStatus({ type: "saving", msg: "Saving record…" });
  try {
    const payload = {
      ...form,
      photos: photos.map(({ url, storagePath, name }) => ({ url, storagePath, name })),
      submittedBy: currentUser?.uid || "",
      submittedByEmail: currentUser?.email || "",
      createdAt: serverTimestamp(),
    };

    // Temporary debug — check what the rules are evaluating
    console.log("=== SAVE DEBUG ===");
    console.log("currentUser:", currentUser);
    console.log("currentUser.uid:", currentUser?.uid);
    console.log("submittedBy in payload:", payload.submittedBy);
    console.log("studentSisId in payload:", payload.studentSisId);
    console.log("studentSisId type:", typeof payload.studentSisId);
    console.log("studentSisId length:", payload.studentSisId?.length);
    console.log("==================");

    await addDoc(collection(db, "studentActions"), payload);
    setStatus({ type: "success", msg: "Record saved successfully." });
  } catch (e) {
    console.error("Full save error:", e);
    setStatus({ type: "error", msg: `Save failed: ${e.message}` });
  }
}

  function loadRecord(record) {
    setViewingRecord(record);
    setForm(record);
    setPhotos(record.photos || []);
    setShowAdminDrawer(false);
    setStatus({ type: "success", msg: `Viewing: ${record.studentDisplayName} — read-only` });
  }

  const readOnly = !!viewingRecord;

  // ── Render ──

  return (
    <div className="srw-root">
      <style>{STYLES}</style>
      <div className="srw-sheet">

        {/* Masthead */}
        <div className="srw-masthead">
          <div>
            <div className="srw-masthead-title">Student Removal — After-Action Record</div>
            <div className="srw-masthead-sub">King's Academy · Madaba, Jordan · Confidential · Complete within 24 hours</div>
          </div>
          <div className="srw-masthead-actions">
            {isAdmin && (
              <button className="srw-btn srw-btn-ghost" onClick={() => setShowAdminDrawer((v) => !v)}>
                {showAdminDrawer ? "Hide" : "Records"}
              </button>
            )}
            {isAdmin && (
              <button className="srw-btn srw-btn-ghost" onClick={() => window.print()}>Print</button>
            )}
            {!readOnly && (
              <button className="srw-btn srw-btn-primary" onClick={handleSave} disabled={status?.type === "saving"}>
                {status?.type === "saving" ? "Saving…" : "Save"}
              </button>
            )}
            {(readOnly || status?.type === "success") && (
              <button className="srw-btn srw-btn-danger" onClick={resetForm}>New</button>
            )}
          </div>
        </div>

        {isAdmin && showAdminDrawer && <AdminDrawer onLoad={loadRecord} />}

        {readOnly && (
          <div className="srw-readonly-banner">
            VIEWING SAVED RECORD — READ ONLY · Tap "New" to start a fresh submission
          </div>
        )}

        {status && (
          <div className={`srw-status ${status.type}`}>
            {status.type === "saving" && "⏳ "}
            {status.type === "success" && "✓ "}
            {status.type === "error" && "✗ "}
            {status.msg}
          </div>
        )}

        <div className="srw-body">

          {/* ── Section 1 — Incident basics ── */}
          <div className="srw-section">
            <SectionLabel>Section 1 — Incident basics</SectionLabel>
            <div className="srw-grid c3">
              <F label="Date of incident" fieldKey="incidentDate" type="date" form={form} setField={setField} readOnly={readOnly} />
              <F label="Time of removal" fieldKey="timeOfRemoval" type="time" form={form} setField={setField} readOnly={readOnly} />
              <F label="Report completed" fieldKey="reportCompletedDate" type="date" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <div className="srw-field">
                <label>Student name (search to select)</label>
                {studentsLoading
                  ? <input className="srw-picker-input" value="Loading students…" readOnly />
                  : (
                    <StudentPicker
                      students={students}
                      value={selectedStudent || (form.studentSisId ? { sisId: form.studentSisId } : null)}
                      onChange={(s) => {
                        setSelectedStudent(s);
                        if (!s) {
                          setForm((prev) => ({
                            ...prev,
                            studentSisId: "",
                            studentDisplayName: "",
                            studentGrade: "",
                            studentIdGrade: "",
                          }));
                        }
                      }}
                      readOnly={readOnly}
                    />
                  )
                }
              </div>
              <div className="srw-field">
                <label>SIS ID / Grade (auto-populated)</label>
                <input value={form.studentIdGrade || ""} readOnly />
              </div>
            </div>
            <div className="srw-grid c3">
                <div className="srw-field">
                <label>Staff initiating removal</label>
                <StaffPicker
                    staff={staff}
                    value={selectedStaff || (form.staffInitiatingSisId ? { sisId: form.staffInitiatingSisId, name: form.staffInitiating } : null)}
                    onChange={(s) => {
                    setSelectedStaff(s);
                    if (!s) {
                        setForm((prev) => ({
                        ...prev,
                        staffInitiating: "",
                        staffInitiatingSisId: "",
                        }));
                    }
                    }}
                    readOnly={readOnly}
                />
                </div>              
              <F label="Administrator notified" fieldKey="administratorNotified" placeholder="Name" form={form} setField={setField} readOnly={readOnly} />
              <F label="Time admin notified" fieldKey="timeAdminNotified" type="time" form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          {/* ── Section 2 — Setting and context ── */}
          <div className="srw-section">
            <SectionLabel>Section 2 — Setting and context</SectionLabel>

            {/* Off-campus toggle */}
            <div style={{ marginBottom: 14, padding: "10px 12px", background: "#f8f4ff", border: "1px solid #c8b0e8", borderRadius: 3 }}>
              <label className="srw-cb-item" style={{ fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!form.offCampus}
                  onChange={readOnly ? undefined : () => setField("offCampus", !form.offCampus)}
                  style={{ width: 20, height: 20 }}
                />
                <span>
                  <strong>Off-campus incident</strong>
                  <span style={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 11, color: "#7040b0", marginLeft: 8 }}>
                    Check if this incident occurred away from King's Academy campus
                  </span>
                </span>
              </label>
            </div>

            <div className="srw-grid c3">
              <S label="Setting type" fieldKey="settingType" options={["Classroom", "Hallway / common area", "Cafeteria", "Gymnasium / PE", "Field trip (off-campus)", "Sports event / competition", "School bus / transport", "Outdoor / playground", "Other"]} form={form} setField={setField} readOnly={readOnly} />
              <F label="Specific location / room" fieldKey="specificLocation" placeholder="e.g. Room 214, Bus #7" form={form} setField={setField} readOnly={readOnly} />
              <F label="Subject / activity in progress" fieldKey="subjectActivity" placeholder="e.g. Math, lunch, away game" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <F label="Other adults present (names)" fieldKey="otherAdultsPresent" placeholder="Names and roles" form={form} setField={setField} readOnly={readOnly} />
              <F label="Approx. students present" fieldKey="studentsPresent" type="number" placeholder="e.g. 28" inputMode="numeric" form={form} setField={setField} readOnly={readOnly} />
            </div>

            {/* ── Off-campus expanded fields ── */}
            {form.offCampus && (
              <div className="srw-offcampus">
                <div className="srw-offcampus-title">🗺️ Off-Campus Details</div>
                <div className="srw-grid c3">
                  <F label="Destination / venue name" fieldKey="offCampusDestination" placeholder="e.g. National Stadium" form={form} setField={setField} readOnly={readOnly} />
                  <F label="City" fieldKey="offCampusCity" placeholder="e.g. Amman" form={form} setField={setField} readOnly={readOnly} />
                  <F label="Country" fieldKey="offCampusCountry" placeholder="e.g. Jordan" form={form} setField={setField} readOnly={readOnly} />
                </div>
                <div className="srw-grid c2">
                  <S label="Type of off-campus activity" fieldKey="offCampusActivityType" options={OFF_CAMPUS_ACTIVITY_TYPES} form={form} setField={setField} readOnly={readOnly} />
                  <F label="Additional supervisors present" fieldKey="offCampusAdditionalSupervisors" placeholder="Names and roles of all KA staff present" form={form} setField={setField} readOnly={readOnly} />
                </div>
                <div className="srw-grid c2">
                  <S
                    label="Signed parental consent / permission slip on file?"
                    fieldKey="offCampusParentConsent"
                    options={[
                      "Yes — physical form on file",
                      "Yes — digital consent recorded",
                      "No — verbal consent obtained",
                      "No — consent status unknown",
                      "Not required for this activity type",
                    ]}
                    form={form} setField={setField} readOnly={readOnly}
                  />
                  <F label="Actual or expected return time to campus" fieldKey="offCampusReturnTime" type="datetime-local" form={form} setField={setField} readOnly={readOnly} />
                </div>
                <div className="srw-grid c2">
                  <F label="Host / venue contact (name and phone)" fieldKey="offCampusHostContact" placeholder="e.g. Coach Ali Hassan, +962-7…" form={form} setField={setField} readOnly={readOnly} />
                  <F label="Transport details (vehicle, driver, plate no.)" fieldKey="offCampusTransportDetails" placeholder="e.g. School bus #4, driver: Ahmad, 12-JO-3456" form={form} setField={setField} readOnly={readOnly} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div className="srw-cb-sublabel">External authorities involved (check all that apply)</div>
                  <CB groupKey="offCampusAuthorities" items={OFF_CAMPUS_AUTHORITY_OPTIONS} form={form} toggleCheck={toggleCheck} readOnly={readOnly} />
                </div>
                <div className="srw-grid c2">
                  <F label="Insurance / incident report reference number" fieldKey="offCampusInsuranceRef" placeholder="If applicable" form={form} setField={setField} readOnly={readOnly} />
                </div>
                <div className="srw-grid">
                  <T label="Additional off-campus context / notes" fieldKey="offCampusNotes" rows={3} placeholder="Jurisdiction-specific considerations, communication with local authorities, venue policies, how the student was transported back to campus, or any logistics that affected the response." form={form} setField={setField} readOnly={readOnly} />
                </div>
              </div>
            )}
          </div>

          {/* ── Section 3 — Nature of the incident ── */}
          <div className="srw-section">
            <SectionLabel>Section 3 — Nature of the incident</SectionLabel>
            <div style={{ marginBottom: 12 }}>
              <div className="srw-cb-sublabel">Primary reason for removal (select all that apply)</div>
              <CB groupKey="incidentReasons" items={INCIDENT_REASONS} form={form} toggleCheck={toggleCheck} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Narrative description — factual, objective, no interpretations" fieldKey="narrative" rows={4} placeholder="Describe the sequence of events in objective terms. Include what was said or done, and by whom." form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <T label="Antecedent / what preceded the behavior" fieldKey="antecedent" rows={3} placeholder="What was happening immediately before? Any known triggers?" form={form} setField={setField} readOnly={readOnly} />
              <T label="Prior redirections attempted today" fieldKey="priorRedirections" rows={3} placeholder="Describe any warnings, verbal redirections, or de-escalation attempts before removal." form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <F label="Witnesses (names)" fieldKey="witnesses" placeholder="Students and/or adults who observed the incident" form={form} setField={setField} readOnly={readOnly} />
              <S label="Were any other students injured or significantly affected?" fieldKey="othersAffected" options={["No", "Yes — describe in narrative"]} form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          {/* ── Photo documentation ── */}
          <div className="srw-section">
            <SectionLabel>Photo Documentation</SectionLabel>
            <div className="srw-photo-note">
              Attach photos of injuries, property damage, or relevant physical evidence.
              Photos are stored securely as part of the confidential record.
              {!readOnly && " On mobile, tap 'Take Photo' to use your camera directly."}
            </div>
            <PhotoUploader
              photos={photos}
              onChange={setPhotos}
              readOnly={readOnly}
              tempFolder={viewingRecord?.id || tempFolder}
            />
          </div>

          {/* ── Mandatory flags + learning plan ── */}
          <div className="srw-section">
            <div className="srw-flag">
              <div className="srw-flag-title">Mandatory reporting flags — check any that apply</div>
              <CB groupKey="mandatoryFlags" items={MANDATORY_FLAGS} form={form} toggleCheck={toggleCheck} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <S label="Does the student have a learning plan on file?" fieldKey="learningPlan" options={["No", "Yes"]} form={form} setField={setField} readOnly={readOnly} />
              {(form.learningPlan === "Yes" || (readOnly && form.learningPlanConsistent)) && (
                <S label="Was behavior consistent with documented accommodations / strategies?" fieldKey="learningPlanConsistent" options={["Yes", "No", "Unclear — requires review"]} form={form} setField={setField} readOnly={readOnly} />
              )}
            </div>
          </div>

          {/* ── Section 4 — Immediate response ── */}
          <div className="srw-section">
            <SectionLabel>Section 4 — Immediate response</SectionLabel>
            <div className="srw-grid c2">
              <S label="Student was sent / taken to" fieldKey="studentSentTo" options={["Main office", "Counselor's office", "Administrator's office", "Designated cool-down space", "Parent / guardian pickup (same day)", "Other"]} form={form} setField={setField} readOnly={readOnly} />
              <F label="Supervised by" fieldKey="supervisedBy" placeholder="Name and role" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <S label="Student's demeanor upon removal" fieldKey="studentDemeanor" options={["Calm / compliant", "Resistant but not physical", "Physically resistant", "Distressed / emotional", "Other"]} form={form} setField={setField} readOnly={readOnly} />
              <S label="Physical restraint used?" fieldKey="physicalRestraint" options={["No", "Yes — document separately per restraint protocol"]} form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Actions taken to support remaining students after removal" fieldKey="actionsForRemaining" rows={2} placeholder="e.g. brief acknowledgment, counselor check-in, transition to new activity" form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          {/* ── Section 5 — Parent / guardian communication ── */}
          <div className="srw-section">
            <SectionLabel>Section 5 — Parent / guardian communication</SectionLabel>
            <div className="srw-info">
              <div className="srw-info-title">Communication protocol</div>
              <CB groupKey="parentContactMethods" items={PARENT_CONTACT_METHODS} form={form} toggleCheck={toggleCheck} readOnly={readOnly} />
            </div>
            <div className="srw-grid c3">
              <F label="Parent / guardian name" fieldKey="parentGuardianName" form={form} setField={setField} readOnly={readOnly} />
              <F label="First contact attempt" fieldKey="firstContactAttempt" type="time" form={form} setField={setField} readOnly={readOnly} />
              <F label="Contact made" fieldKey="contactMadeTime" type="time" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Summary of conversation / message left" fieldKey="conversationSummary" rows={3} placeholder="What was communicated? What was the parent's response?" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <S label="Follow-up meeting scheduled?" fieldKey="followUpMeeting" options={["No", "Yes — date/time below"]} form={form} setField={setField} readOnly={readOnly} />
              <F label="Meeting date / time (if applicable)" fieldKey="meetingDateTime" type="datetime-local" form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          {/* ── Section 6 — Next steps ── */}
          <div className="srw-section">
            <SectionLabel>Section 6 — Next steps and expectations</SectionLabel>
            <div className="srw-grid c2">
              <S
                label="Disciplinary consequence assigned"
                fieldKey="disciplinaryConsequence"
                options={["Verbal warning on record", "Loss of privilege", "Detention", "In-school suspension (ISS)", "Out-of-school suspension (OSS)", "Exclusion from specific future events", "Referral to counselor / support services", "Referral to threat assessment team", "Law enforcement referral", "Pending review — no consequence yet"]}
                form={form} setField={setField} readOnly={readOnly}
              />
              <F label="Duration / specifics of consequence" fieldKey="consequenceDuration" placeholder="e.g. 2-day OSS, excluded from next 3 away games" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Conditions for return to the setting" fieldKey="conditionsForReturn" rows={3} placeholder="e.g. meeting with counselor completed, written apology delivered, parent conference held, behavior plan updated" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Behavioral expectations upon return (specific and observable)" fieldKey="behavioralExpectations" rows={3} placeholder="e.g. 'Student will raise hand before speaking, stay in assigned seat…'" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <S label="Behavior support plan update needed?" fieldKey="behaviorSupportPlan" options={["No", "Yes — existing plan to be reviewed", "Yes — new plan to be developed"]} form={form} setField={setField} readOnly={readOnly} />
              <S label="Counseling / mental health referral?" fieldKey="counselingReferral" options={["No", "Yes — school counselor", "Yes — external referral recommended"]} form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Check-in / monitoring plan (who, how often, for how long)" fieldKey="checkInPlan" rows={2} placeholder="e.g. daily check-in with counselor for 2 weeks, weekly update to admin for 30 days" form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          {/* ── Academic continuity ── */}
          <div className="srw-section">
            <div className="srw-warn">
              <div className="srw-warn-title">Academic continuity</div>
              <CB groupKey="academicContinuity" items={ACADEMIC_CONTINUITY} form={form} toggleCheck={toggleCheck} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Notes on academic continuity plan" fieldKey="academicContinuityNotes" rows={2} placeholder="Describe any arrangements made to minimize academic impact." form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          {/* ── Section 7 — Reflection ── */}
          <div className="srw-section">
            <SectionLabel>Section 7 — Reflection and systemic notes</SectionLabel>
            <div className="srw-grid">
              <T label="What factors may have contributed to this incident?" fieldKey="contributingFactors" rows={3} placeholder="Avoid assigning blame — consider antecedents, stressors, unmet needs, or gaps in support." form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid c2">
              <S label="Was there a pattern — has this or similar behavior occurred before?" fieldKey="patternHistory" options={["No — isolated incident", "Yes — same behavior repeated", "Yes — escalating pattern", "Unknown — check records"]} form={form} setField={setField} readOnly={readOnly} />
              <F label="Previous incident dates (if applicable)" fieldKey="previousIncidentDates" placeholder="e.g. 10/4, 11/1" form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="What, if anything, could staff or the school have done differently?" fieldKey="staffReflection" rows={3} placeholder="Honest self-assessment — for internal use and professional growth." form={form} setField={setField} readOnly={readOnly} />
            </div>
            <div className="srw-grid">
              <T label="Any systemic or environmental changes recommended?" fieldKey="systemicChanges" rows={2} placeholder="e.g. seating change, schedule adjustment, modified field trip policy" form={form} setField={setField} readOnly={readOnly} />
            </div>
          </div>

          <hr className="srw-divider" />

          {/* ── Signatures ── */}
          <div className="srw-section">
            <SectionLabel>Signatures</SectionLabel>
            <div className="srw-sign-row">
              <div className="srw-sign-field">Initiating staff signature / date</div>
              <div className="srw-sign-field">Supervising administrator signature / date</div>
              <div className="srw-sign-field">Counselor (if involved) / date</div>
            </div>
          </div>

        </div>{/* end srw-body */}

        <div className="srw-footer">
          Confidential student record · King's Academy, Madaba, Jordan · Handle per school data retention policy
          {viewingRecord?.submittedByEmail && <> · Submitted by: {viewingRecord.submittedByEmail}</>}
        </div>

      </div>
    </div>
  );
}